import { useCallback, useEffect, useState } from "react";
import {
    Award, ChevronRight, Phone, Mail, MapPin, Clock, Calendar, Star, PlayCircle,
    Briefcase, GraduationCap, UserCheck, UserRound, Wallet, TrendingUp, Sparkles,
    Home as HomeIcon, Building2, Hourglass, ShieldCheck, Egg, Quote, MessageCircle, Volume2, VolumeX, Check,
} from "lucide-react";
import { getSeminar, getVideos, getMentor, listStories, listReviews } from "@/lib/api";
import { Reveal, Stagger, Item } from "@/components/Reveal";
import Counter from "@/components/Counter";
import Countdown from "@/components/Countdown";
import StickyMobileBar from "@/components/StickyMobileBar";
import AnnouncementBar from "@/components/AnnouncementBar";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { parseYouTubeId, ytEmbed, ytThumb, isMp4, isShorts } from "@/lib/video";
import { Card } from "@/components/ui/card";

const iconMap = {
    briefcase: Briefcase,
    "graduation-cap": GraduationCap,
    "user-check": UserCheck,
    "user-round": UserRound,
    wallet: Wallet,
    "trending-up": TrendingUp,
    sparkles: Sparkles,
    home: HomeIcon,
    "building-2": Building2,
    hourglass: Hourglass,
};
// Two-tone bold hero headline — first half navy, second half orange.
function HeroHeadline({ title = "" }) {
    const fallback = "B2B व D2C व्यवसायातून\nउद्योजक होण्याची सुवर्णसंधी";
    const text = title || fallback;
    let first, second;
    if (text.includes("\n")) {
        const idx = text.indexOf("\n");
        first = text.slice(0, idx);
        second = text.slice(idx + 1);
    } else {
        const words = text.split(/\s+/);
        const mid = Math.ceil(words.length / 2);
        first = words.slice(0, mid).join(" ");
        second = words.slice(mid).join(" ");
    }
    return (
        <h1 className="mt-6 marathi leading-[1.25] md:leading-[1.2] text-5xl sm:text-6xl lg:text-7xl font-black tracking-tight" style={{ letterSpacing: "-0.02em", wordSpacing: "0.05em" }}>
            <span className="block text-navy mb-2">{first}</span>
            {second && <span className="block text-orange-brand">{second}</span>}
        </h1>
    );
}

function CountdownBlock({ seminar, onRegister }) {
    // Use admin-set deadline; fallback to 3 days from now
    const target = seminar?.registration_deadline || new Date(Date.now() + 3 * 24 * 60 * 60 * 1000).toISOString();
    return (
        <section className="relative overflow-hidden">
            <div className="container-max">
                <Reveal>
                    <div className="relative rounded-3xl bg-navy-gradient text-white overflow-hidden shadow-lift">
                        <div className="absolute inset-0 bg-grain opacity-30 mix-blend-overlay" />
                        <div className="absolute -top-24 -right-24 w-[400px] h-[400px] rounded-full bg-brand-orange/25 blur-3xl" />
                        <div className="absolute -bottom-24 -left-24 w-[400px] h-[400px] rounded-full bg-brand-orange/15 blur-3xl" />
                        <div className="relative p-8 md:p-12 grid md:grid-cols-2 gap-8 items-center">
                            <div>
                                <span className="inline-flex items-center gap-2 rounded-full bg-brand-orange text-white px-4 py-1.5 text-xs uppercase tracking-widest font-extrabold shadow-orange">
                                    <Hourglass className="w-3.5 h-3.5" /> Offer Ends Soon
                                </span>
                                <h3 className="mt-4 text-3xl md:text-4xl font-extrabold text-white marathi leading-tight" style={{letterSpacing:'-0.02em'}}>
                                    सुवर्णसंधी हुकवू नका — <span className="text-brand-orangeLight">आजच नोंदणी करा</span>
                                </h3>
                                <p className="mt-3 text-white/80 text-sm md:text-base marathi">
                                    फक्त ₹{seminar?.registration_fee || "199"} मध्ये seat book करा. मर्यादित जागा उपलब्ध.
                                </p>
                                <button
                                    onClick={onRegister}
                                    data-testid="countdown-cta"
                                    className="btn-gold mt-6 text-base"
                                >
                                    Book My Seat <ChevronRight className="w-5 h-5" />
                                </button>
                            </div>
                            <div className="flex flex-col items-center md:items-end gap-3">
                                <div className="text-[11px] uppercase tracking-widest text-white/70 font-bold">Registration Closes In</div>
                                <Countdown target={target} />
                            </div>
                        </div>
                    </div>
                </Reveal>
            </div>
            <div className="h-16" />
        </section>
    );
}


function TopBar({ seminar, onRegister }) {
    return (
        <div className="sticky top-0 z-30 bg-white/90 backdrop-blur-xl border-b border-slate-200">
            <div className="container-max flex items-center justify-between py-3">
                <div className="flex items-center gap-2.5">
                    <div className="w-10 h-10 rounded-xl bg-orange-gradient flex items-center justify-center shadow-orange">
                        <Egg className="w-5 h-5 text-white" />
                    </div>
                    <div className="leading-tight">
                        <div className="font-extrabold text-lg text-navy">Energy Plus</div>
                        <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold">Premium Brown Eggs</div>
                    </div>
                </div>
                <div className="hidden md:flex items-center gap-6 text-sm font-semibold text-slate-600">
                </div>
                <button data-testid="top-register-btn" onClick={onRegister} className="btn-gold !py-2.5 !px-5 text-sm">
                    Register ₹{seminar?.registration_fee || "199"} <ChevronRight className="w-4 h-4" />
                </button>
            </div>
        </div>
    );
}

function HeroVideo({ url, autoplay, muted: initialMuted }) {
    const id = parseYouTubeId(url);
    const mp4 = isMp4(url);
    const [muted, setMuted] = useState(!!initialMuted);
    if (!url) return null;
    return (
        <div className="relative rounded-3xl overflow-hidden border border-brand-stone shadow-lift bg-black aspect-video">
            {mp4 ? (
                <video
                    key={url}
                    src={url}
                    autoPlay={autoplay}
                    muted={muted}
                    loop
                    playsInline
                    className="w-full h-full object-cover"
                    controls
                />
            ) : id ? (
                <iframe
                    key={url + (muted ? "-m" : "-u")}
                    title="Hero video"
                    src={ytEmbed(url, { autoplay, mute: muted })}
                    className="absolute inset-0 w-full h-full"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                />
            ) : null}
            <button
                onClick={() => setMuted((m) => !m)}
                className="absolute bottom-3 right-3 z-10 rounded-full bg-black/60 backdrop-blur px-3 py-1.5 text-white text-xs flex items-center gap-1.5"
                data-testid="hero-mute-toggle"
            >
                {muted ? <VolumeX className="w-3.5 h-3.5" /> : <Volume2 className="w-3.5 h-3.5" />}
                {muted ? "Unmute" : "Mute"}
            </button>
        </div>
    );
}

function ClickToPlayVideo({ url, title }) {
    const [playing, setPlaying] = useState(false);
    if (!url) return null;
    const id = parseYouTubeId(url);
    const mp4 = isMp4(url);
    const thumb = ytThumb(url);
    const shortsClass = isShorts(url) ? "aspect-[9/16] max-w-sm mx-auto" : "aspect-video";
    return (
        <div className={`relative rounded-3xl overflow-hidden border border-brand-stone shadow-lift bg-black ${shortsClass}`}>
            {!playing && thumb ? (
                <button onClick={() => setPlaying(true)} className="absolute inset-0 group" aria-label={title}>
                    <img src={thumb} alt={title || "Video"} className="w-full h-full object-cover" loading="lazy" />
                    <div className="absolute inset-0 bg-black/30 group-hover:bg-black/40 transition-colors flex items-center justify-center">
                        <div className="w-20 h-20 rounded-full bg-gold-gradient flex items-center justify-center shadow-gold group-hover:scale-110 transition-transform">
                            <PlayCircle className="w-10 h-10 text-white" />
                        </div>
                    </div>
                </button>
            ) : mp4 ? (
                <video key={url} src={url} controls autoPlay className="w-full h-full object-cover" />
            ) : id && playing ? (
                <iframe
                    key={url}
                    title={title}
                    src={ytEmbed(url, { autoplay: true })}
                    className="absolute inset-0 w-full h-full"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                />
            ) : (
                <div className="absolute inset-0 flex items-center justify-center text-white/60 text-sm">No video</div>
            )}
        </div>
    );
}

export default function Landing() {
    const [seminar, setSeminar] = useState(null);
    const [videos, setVideos] = useState(null);
    const [mentor, setMentor] = useState(null);
    const [stories, setStories] = useState([]);
    const [reviews, setReviews] = useState([]);
    const [formOpen, setFormOpen] = useState(false);
    const [qrCopied, setQrCopied] = useState(false);

    const refresh = useCallback(async () => {
        try {
            const [s, v, m, st, rv] = await Promise.all([
                getSeminar(), getVideos(), getMentor(), listStories(), listReviews(),
            ]);
            setSeminar(s); setVideos(v); setMentor(m); setStories(st); setReviews(rv);
        } catch {}
    }, []);

    useEffect(() => {
        refresh();
        const onVis = () => { if (document.visibilityState === "visible") refresh(); };
        const onFocus = () => refresh();
        const onStorage = (e) => { if (e.key === "cms_updated") refresh(); };
        document.addEventListener("visibilitychange", onVis);
        window.addEventListener("focus", onFocus);
        window.addEventListener("storage", onStorage);
        return () => {
            document.removeEventListener("visibilitychange", onVis);
            window.removeEventListener("focus", onFocus);
            window.removeEventListener("storage", onStorage);
        };
    }, [refresh]);

    // Register ₹199 CTA — opens QR payment modal
    const scrollToForm = () => setFormOpen(true);

    const benefits = seminar?.benefits || [];
    const s3 = seminar?.section3_items || [];
    const s4 = seminar?.section4_items || [];
    const s2 = seminar?.section2_items || [];

    const heroImage = seminar?.hero_image || "https://images.pexels.com/photos/37919439/pexels-photo-37919439.jpeg";

    return (
        <div className="min-h-screen bg-white text-brand-ink">
            {seminar && (
                <>
                    <title>{seminar.meta_title || "Energy Plus Premium Brown Eggs"}</title>
                    <meta name="description" content={seminar.meta_description || ""} />
                </>
            )}

            <AnnouncementBar businessName="Energy Plus Premium Brown Eggs" fee={seminar?.registration_fee || "199"} />
            <TopBar seminar={seminar} onRegister={scrollToForm} />

            {/* HERO */}
            <section className="relative overflow-hidden bg-white">
                {/* Dot pattern background */}
                <div className="absolute inset-0 -z-10 bg-dots [background-size:22px_22px] opacity-70" />
                <div className="absolute inset-0 -z-10 bg-[radial-gradient(60%_50%_at_90%_10%,rgba(242,102,34,0.08),transparent),radial-gradient(50%_40%_at_5%_90%,rgba(21,43,122,0.06),transparent)]" />
                <div className="container-max pt-10 md:pt-16 pb-16 md:pb-24 grid lg:grid-cols-12 gap-10 items-center">
                    <div className="lg:col-span-7 order-2 lg:order-1">
                        <Reveal>
                            <span className="chip">
                                <Sparkles className="w-3.5 h-3.5" />
                                Franchise & Distribution Opportunity
                            </span>
                        </Reveal>
                        <Reveal delay={0.1}>
                            <HeroHeadline title={seminar?.hero_title} />
                        </Reveal>
                        <Reveal delay={0.2}>
                            <p className="mt-6 text-lg md:text-xl text-slate-600 max-w-2xl marathi leading-relaxed">
                                {seminar?.hero_subtitle}
                            </p>
                        </Reveal>

                        <Reveal delay={0.3}>
                            <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-xl">
                                {benefits.map((b, i) => (
                                    <div key={i} className="flex items-center gap-3">
                                        <div className="w-8 h-8 rounded-full bg-emerald-50 border border-emerald-200 flex items-center justify-center shrink-0">
                                            <Check className="w-4 h-4 text-emerald-600" strokeWidth={3} />
                                        </div>
                                        <span className="text-base font-semibold text-slate-800 marathi">{b}</span>
                                    </div>
                                ))}
                            </div>
                        </Reveal>

                        <Reveal delay={0.4}>
                            <div className="mt-10 flex flex-wrap items-center gap-4">
                                <button data-testid="hero-cta" onClick={scrollToForm} className="btn-gold text-base md:text-lg">
                                    {seminar?.hero_cta || "आता नोंदणी करा"} — फक्त ₹{seminar?.registration_fee || "199"} मध्ये
                                    <ChevronRight className="w-5 h-5" />
                                </button>
                                {seminar?.original_fee && seminar?.original_fee !== seminar?.registration_fee && (
                                    <div className="flex items-center gap-2 text-sm text-slate-500">
                                        <span className="line-through">₹{seminar.original_fee}</span>
                                        <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 text-emerald-700 px-2.5 py-1 text-xs font-semibold border border-emerald-200">
                                            <ShieldCheck className="w-3.5 h-3.5" /> 100% Secure Payment
                                        </span>
                                    </div>
                                )}
                            </div>
                        </Reveal>

                        <Reveal delay={0.5}>
                            <div className="mt-10 grid grid-cols-3 max-w-lg gap-4 text-center">
                                <div>
                                    <div className="text-2xl md:text-3xl text-navy font-extrabold">
                                        <Counter to={5000} suffix="+" />
                                    </div>
                                    <div className="text-xs uppercase tracking-widest text-slate-500 mt-1">Participants</div>
                                </div>
                                <div className="border-x border-brand-stone">
                                    <div className="text-2xl md:text-3xl text-navy font-extrabold">
                                        <Counter to={500} suffix="+" />
                                    </div>
                                    <div className="text-xs uppercase tracking-widest text-slate-500 mt-1">Businesses</div>
                                </div>
                                <div>
                                    <div className="text-2xl md:text-3xl text-navy font-extrabold">
                                        <Counter to={10} suffix="+" />
                                    </div>
                                    <div className="text-xs uppercase tracking-widest text-slate-500 mt-1">Years</div>
                                </div>
                            </div>
                        </Reveal>
                    </div>

                    <div className="lg:col-span-5 order-1 lg:order-2">
                        <Reveal delay={0.15}>
                            <div className="relative">
                                <div className="absolute -inset-4 bg-orange-gradient opacity-15 rounded-[3rem] blur-2xl -z-10" />
                                {videos?.hero_video_url ? (
                                    <HeroVideo url={videos.hero_video_url} autoplay={videos.hero_autoplay} muted={videos.hero_muted} />
                                ) : (
                                    <div className="relative rounded-[2rem] overflow-hidden border-2 border-white shadow-lift ring-1 ring-slate-100 bg-navy-gradient aspect-video flex flex-col items-center justify-center text-center p-8">
                                        <div className="w-16 h-16 rounded-full bg-white/15 border border-white/20 flex items-center justify-center mb-4">
                                            <PlayCircle className="w-8 h-8 text-white" />
                                        </div>
                                        <div className="text-white font-bold text-lg">Welcome Video</div>
                                        <div className="text-white/70 text-sm mt-1 max-w-xs">Admin → Dashboard → Videos tab मध्ये YouTube link add करा</div>
                                    </div>
                                )}
                            </div>
                        </Reveal>
                    </div>
                </div>
            </section>

            {/* SECTION 2 - Workshop */}
            <section id="what" className="section-pad bg-slate-50">
                <div className="container-max">
                    <Reveal>
                        <div className="text-center max-w-2xl mx-auto">
                            <span className="chip"><Award className="w-3.5 h-3.5" /> Curriculum</span>
                            <h2 className="heading-serif text-3xl sm:text-4xl lg:text-5xl mt-4 marathi">
                                {seminar?.section2_title}
                            </h2>
                            <div className="divider-gold mx-auto mt-4" />
                        </div>
                    </Reveal>
                    <Stagger className="mt-14 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {s2.map((t, i) => (
                            <Item key={i}>
                                <div className="card-luxe p-7 h-full">
                                    <div className="w-12 h-12 rounded-2xl bg-brand-orangeLight border border-brand-orange/20 flex items-center justify-center mb-4">
                                        <span className="text-brand-orange font-extrabold text-lg">{String(i + 1).padStart(2, "0")}</span>
                                    </div>
                                    <div className="text-lg font-bold text-navy marathi leading-snug">{t}</div>
                                    <div className="mt-3 text-sm text-slate-600 font-medium">Practical, step-by-step guidance directly from the mentor.</div>
                                </div>
                            </Item>
                        ))}
                    </Stagger>
                </div>
            </section>

            {/* SECTION 3 - Audience */}
            <section id="who" className="section-pad bg-white">
                <div className="container-max">
                    <Reveal>
                        <div className="text-center max-w-2xl mx-auto">
                            <span className="chip"><UserRound className="w-3.5 h-3.5" /> Who Should Attend</span>
                            <h2 className="heading-serif text-3xl sm:text-4xl lg:text-5xl mt-4 marathi">
                                {seminar?.section3_title}
                            </h2>
                            <div className="divider-gold mx-auto mt-4" />
                        </div>
                    </Reveal>
                    <Stagger className="mt-14 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
                        {s3.map((it, i) => {
                            const Ico = iconMap[it.icon] || UserRound;
                            return (
                                <Item key={i}>
                                    <div className="card-luxe p-6 text-center h-full">
                                        <div className="w-14 h-14 mx-auto rounded-2xl bg-orange-gradient flex items-center justify-center shadow-orange">
                                            <Ico className="w-6 h-6 text-white" />
                                        </div>
                                        <div className="mt-4 text-sm md:text-base font-bold text-navy marathi">{it.text}</div>
                                    </div>
                                </Item>
                            );
                        })}
                    </Stagger>
                </div>
            </section>

            {/* SECTION 4 - Why attend */}
            <section className="section-pad bg-slate-50">
                <div className="container-max">
                    <Reveal>
                        <div className="text-center max-w-2xl mx-auto">
                            <span className="chip"><Sparkles className="w-3.5 h-3.5" /> Why Attend</span>
                            <h2 className="heading-serif text-3xl sm:text-4xl lg:text-5xl mt-4 marathi">
                                {seminar?.section4_title}
                            </h2>
                            <div className="divider-gold mx-auto mt-4" />
                        </div>
                    </Reveal>
                    <Stagger className="mt-14 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {s4.map((it, i) => {
                            const Ico = iconMap[it.icon] || Sparkles;
                            return (
                                <Item key={i}>
                                    <div className="card-luxe p-7 h-full flex gap-5 items-start">
                                        <div className="w-14 h-14 rounded-2xl bg-brand-orangeLight border border-brand-orange/20 flex items-center justify-center shrink-0">
                                            <Ico className="w-6 h-6 text-brand-orange" />
                                        </div>
                                        <div>
                                            <div className="text-lg font-bold text-navy">{it.text}</div>
                                            <div className="mt-2 text-sm text-slate-600 font-medium">A structured pathway to building a scalable, replicable business model.</div>
                                        </div>
                                    </div>
                                </Item>
                            );
                        })}
                    </Stagger>
                </div>
            </section>

            {/* SECTION 5 - Info */}
            <section className="section-pad bg-white">
                <div className="container-max">
                    <Reveal>
                        <div className="text-center max-w-2xl mx-auto">
                            <span className="chip"><Calendar className="w-3.5 h-3.5" /> Seminar Details</span>
                            <h2 className="heading-serif text-3xl sm:text-4xl lg:text-5xl mt-4 marathi">कार्यशाळेची माहिती</h2>
                            <div className="divider-gold mx-auto mt-4" />
                        </div>
                    </Reveal>
                    <Reveal delay={0.15}>
                        <div className="mt-12 max-w-4xl mx-auto card-luxe p-8 md:p-10">
                            <div className="grid md:grid-cols-3 gap-6">
                                <div className="flex items-start gap-4">
                                    <div className="w-12 h-12 rounded-2xl bg-orange-gradient flex items-center justify-center shrink-0 shadow-orange">
                                        <Calendar className="w-6 h-6 text-white" />
                                    </div>
                                    <div>
                                        <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Date</div>
                                        <div className="text-lg font-bold text-navy marathi mt-1">{seminar?.date_text}</div>
                                    </div>
                                </div>
                                <div className="flex items-start gap-4">
                                    <div className="w-12 h-12 rounded-2xl bg-orange-gradient flex items-center justify-center shrink-0 shadow-orange">
                                        <Clock className="w-6 h-6 text-white" />
                                    </div>
                                    <div>
                                        <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Time</div>
                                        <div className="text-lg font-bold text-navy marathi mt-1">{seminar?.time_text}</div>
                                    </div>
                                </div>
                                <div className="flex items-start gap-4">
                                    <div className="w-12 h-12 rounded-2xl bg-orange-gradient flex items-center justify-center shrink-0 shadow-orange">
                                        <MapPin className="w-6 h-6 text-white" />
                                    </div>
                                    <div>
                                        <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Venue</div>
                                        <div className="text-lg font-bold text-navy mt-1">{seminar?.venue}</div>
                                        {seminar?.google_map_url && (
                                            <a
                                                data-testid="map-link"
                                                href={seminar.google_map_url}
                                                target="_blank"
                                                rel="noreferrer"
                                                className="text-xs text-brand-orange font-semibold mt-1 inline-flex items-center gap-1"
                                            >
                                                View on Google Maps <ChevronRight className="w-3 h-3" />
                                            </a>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </Reveal>
                </div>
            </section>

            {/* SECTION 6 - Mentor */}
            <section id="mentor" className="section-pad bg-slate-50">
                <div className="container-max grid lg:grid-cols-2 gap-14 items-center">
                    <Reveal>
                        <div className="relative">
                            <div className="absolute -inset-4 bg-orange-gradient opacity-10 rounded-[3rem] blur-2xl -z-10" />
                            <div className="grid grid-cols-5 gap-3">
                                <div className="col-span-3 rounded-3xl overflow-hidden border-2 border-white shadow-lift aspect-[3/4]">
                                    <img src={mentor?.primary_image} alt={mentor?.name} className="w-full h-full object-cover" loading="lazy" />
                                </div>
                                <div className="col-span-2 flex flex-col gap-3">
                                    {mentor?.secondary_image ? (
                                        <div className="rounded-3xl overflow-hidden border-2 border-white shadow-soft aspect-square">
                                            <img src={mentor.secondary_image} alt="" className="w-full h-full object-cover" loading="lazy" />
                                        </div>
                                    ) : (
                                        <div className="rounded-3xl bg-white border border-slate-200 p-5 aspect-square flex flex-col items-center justify-center text-center shadow-soft">
                                            <div className="text-3xl font-extrabold text-brand-orange">
                                                <Counter to={mentor?.experience || 10} suffix="+" />
                                            </div>
                                            <div className="text-xs text-slate-600 font-medium mt-1">Years in industry</div>
                                        </div>
                                    )}
                                    <div className="rounded-3xl bg-white border border-slate-200 p-5 aspect-square flex flex-col items-center justify-center text-center shadow-soft">
                                        <div className="text-3xl font-extrabold text-brand-orange">
                                            <Counter to={mentor?.businesses_guided || 500} suffix="+" />
                                        </div>
                                        <div className="text-xs text-slate-600 font-medium mt-1">Businesses Guided</div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </Reveal>
                    <Reveal delay={0.15}>
                        <div>
                            <span className="chip"><Award className="w-3.5 h-3.5" /> Meet Your Mentor</span>
                            <h2 className="heading-serif text-3xl sm:text-4xl lg:text-5xl mt-4">
                                {mentor?.name}
                            </h2>
                            <div className="text-slate-600 font-semibold mt-2">{mentor?.designation}</div>
                            <div className="divider-gold mt-5" />

                            <blockquote className="mt-6 rounded-2xl bg-white border border-slate-200 p-6 italic text-navy relative shadow-soft">
                                <Quote className="absolute -top-3 -left-3 w-8 h-8 text-white bg-orange-gradient rounded-full p-1.5 shadow-orange" />
                                <span className="marathi font-semibold">{mentor?.mission_statement}</span>
                            </blockquote>

                            <p className="mt-6 text-slate-600 leading-relaxed">{mentor?.about}</p>

                            {mentor?.achievements?.length ? (
                                <ul className="mt-6 space-y-2">
                                    {mentor.achievements.map((a, i) => (
                                        <li key={i} className="flex items-start gap-2 text-sm">
                                            <div className="w-5 h-5 rounded-full bg-emerald-50 border border-emerald-200 flex items-center justify-center shrink-0 mt-0.5">
                                                <Check className="w-3 h-3 text-emerald-600" strokeWidth={3} />
                                            </div>
                                            <span className="font-medium text-slate-700">{a}</span>
                                        </li>
                                    ))}
                                </ul>
                            ) : null}

                            {videos?.mentor_video_url && (
                                <div className="mt-8">
                                    <ClickToPlayVideo url={videos.mentor_video_url} title="Mentor" />
                                </div>
                            )}

                            <button onClick={scrollToForm} data-testid="mentor-cta" className="btn-gold mt-8">
                                {mentor?.cta_text || "Book Your Seat"} <ChevronRight className="w-5 h-5" />
                            </button>
                        </div>
                    </Reveal>
                </div>
            </section>

            {/* SUCCESS STORIES */}
            {stories.length > 0 && (
                <section id="stories" className="section-pad bg-brand-cream">
                    <div className="container-max">
                        <Reveal>
                            <div className="text-center max-w-2xl mx-auto">
                                <span className="chip"><TrendingUp className="w-3.5 h-3.5 text-brand-gold" /> Success Stories</span>
                                <h2 className="heading-serif text-3xl sm:text-4xl lg:text-5xl mt-4 marathi">यांनी सुरू केले, आता तुमची पाळी</h2>
                                <div className="divider-gold mx-auto mt-4" />
                            </div>
                        </Reveal>
                        <Stagger className="mt-14 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {stories.map((st) => (
                                <Item key={st.id}>
                                    <div className="card-luxe p-6 h-full">
                                        {st.youtube_url ? (
                                            <div className="mb-4 rounded-2xl overflow-hidden">
                                                <ClickToPlayVideo url={st.youtube_url} title={st.name} />
                                            </div>
                                        ) : st.photo ? (
                                            <div className="mb-4 rounded-2xl overflow-hidden aspect-video">
                                                <img src={st.photo} alt={st.name} className="w-full h-full object-cover" loading="lazy" />
                                            </div>
                                        ) : null}
                                        <div className="flex items-center gap-3">
                                            {st.photo && !st.youtube_url ? null : st.photo ? (
                                                <img src={st.photo} alt="" className="w-11 h-11 rounded-full object-cover" />
                                            ) : (
                                                <div className="w-11 h-11 rounded-full bg-orange-gradient flex items-center justify-center text-white font-bold shadow-orange">
                                                    {st.name?.[0] || "?"}
                                                </div>
                                            )}
                                            <div>
                                                <div className="font-bold text-navy">{st.name}</div>
                                                <div className="text-xs text-slate-500 font-medium">{st.city}</div>
                                            </div>
                                            {st.tag && (
                                                <span className="ml-auto text-[10px] uppercase tracking-widest text-brand-orange bg-brand-orangeLight border border-brand-orange/20 rounded-full px-2 py-1 font-semibold">
                                                    {st.tag}
                                                </span>
                                            )}
                                        </div>
                                        {st.description && (
                                            <p className="mt-4 text-sm text-slate-600 leading-relaxed">{st.description}</p>
                                        )}
                                    </div>
                                </Item>
                            ))}
                        </Stagger>
                    </div>
                </section>
            )}

            {/* REVIEWS */}
            {reviews.length > 0 && (
                <section className="section-pad bg-slate-50">
                    <div className="container-max">
                        <Reveal>
                            <div className="text-center max-w-2xl mx-auto">
                                <span className="chip"><Star className="w-3.5 h-3.5" /> Reviews</span>
                                <h2 className="heading-serif text-3xl sm:text-4xl lg:text-5xl mt-4 marathi">Attendees काय म्हणतात</h2>
                                <div className="divider-gold mx-auto mt-4" />
                            </div>
                        </Reveal>
                        <Stagger className="mt-14 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {reviews.map((r) => (
                                <Item key={r.id}>
                                    <div className="card-luxe p-6 h-full flex flex-col">
                                        <div className="flex items-center gap-1 mb-3">
                                            {Array.from({ length: 5 }).map((_, i) => (
                                                <Star key={i} className={`w-4 h-4 ${i < (r.rating || 5) ? "fill-brand-gold text-brand-gold" : "text-brand-stone"}`} />
                                            ))}
                                        </div>
                                        {r.youtube_url && (
                                            <div className="mb-3 rounded-2xl overflow-hidden">
                                                <ClickToPlayVideo url={r.youtube_url} title={r.name} />
                                            </div>
                                        )}
                                        <p className="text-sm text-navy italic leading-relaxed flex-1 font-medium">"{r.text}"</p>
                                        <div className="flex items-center gap-3 mt-4 pt-4 border-t border-slate-100">
                                            {r.photo ? (
                                                <img src={r.photo} alt="" className="w-10 h-10 rounded-full object-cover" />
                                            ) : (
                                                <div className="w-10 h-10 rounded-full bg-brand-orangeLight border border-brand-orange/20 flex items-center justify-center font-bold text-brand-orange">
                                                    {r.name?.[0] || "?"}
                                                </div>
                                            )}
                                            <div>
                                                <div className="font-bold text-navy text-sm">{r.name}</div>
                                                <div className="text-[11px] text-slate-500 font-medium">{r.city}</div>
                                            </div>
                                        </div>
                                    </div>
                                </Item>
                            ))}
                        </Stagger>
                    </div>
                </section>
            )}

            {/* URGENCY */}
            <section className="relative overflow-hidden bg-navy-gradient text-white section-pad">
                <div className="absolute inset-0 bg-grain opacity-30 mix-blend-overlay" />
                <div className="absolute -top-40 -right-40 w-[500px] h-[500px] rounded-full bg-brand-orange/25 blur-3xl" />
                <div className="absolute -bottom-40 -left-40 w-[500px] h-[500px] rounded-full bg-brand-orange/15 blur-3xl" />
                <div className="container-max relative text-center">
                    <Reveal>
                        <span className="inline-flex items-center gap-2 rounded-full bg-brand-orange px-4 py-2 text-xs uppercase tracking-widest text-white font-bold shadow-orange">
                            <Hourglass className="w-3.5 h-3.5" /> Limited Seats
                        </span>
                    </Reveal>
                    <Reveal delay={0.1}>
                        <h2 className="text-4xl sm:text-5xl lg:text-6xl mt-6 marathi font-extrabold text-white tracking-tight" style={{letterSpacing:'-0.02em'}}>
                            {seminar?.urgency_heading}
                        </h2>
                    </Reveal>
                    <Reveal delay={0.2}>
                        <p className="mt-5 text-white/80 max-w-2xl mx-auto marathi text-lg">{seminar?.urgency_description}</p>
                    </Reveal>

                    {seminar?.registration_deadline && (
                        <Reveal delay={0.3}>
                            <div className="mt-10 inline-flex flex-col items-center gap-3">
                                <div className="text-xs uppercase tracking-widest text-white/70 font-bold">Registration Closes In</div>
                                <Countdown target={seminar.registration_deadline} />
                            </div>
                        </Reveal>
                    )}

                    <Reveal delay={0.35}>
                        <div className="mt-10 inline-flex items-center gap-4 rounded-2xl border border-white/20 bg-white/10 backdrop-blur px-6 py-4">
                            <div>
                                <div className="text-[11px] uppercase tracking-widest text-white/70 font-bold">Registration Fee</div>
                                <div className="text-3xl font-extrabold text-white">₹{seminar?.registration_fee || "199"}</div>
                            </div>
                            {seminar?.original_fee && seminar?.original_fee !== seminar?.registration_fee && (
                                <div className="text-lg line-through text-white/60">₹{seminar.original_fee}</div>
                            )}
                        </div>
                    </Reveal>
                    <Reveal delay={0.4}>
                        <div className="mt-10">
                            <button data-testid="urgency-cta" onClick={scrollToForm} className="btn-gold text-lg">
                                {seminar?.urgency_cta} <ChevronRight className="w-5 h-5" />
                            </button>
                        </div>
                    </Reveal>
                </div>
            </section>

            {/* CONTACT */}
            <section id="contact" className="section-pad bg-white">
                <div className="container-max">
                    <Reveal>
                        <div className="text-center max-w-2xl mx-auto">
                            <span className="chip"><Mail className="w-3.5 h-3.5" /> Contact</span>
                            <h2 className="heading-serif text-3xl sm:text-4xl lg:text-5xl mt-4">Get in Touch</h2>
                            <div className="divider-gold mx-auto mt-4" />
                            <p className="mt-4 text-slate-600 font-semibold">Business Mentor</p>
                            <div className="mt-1 text-2xl font-extrabold text-navy">{seminar?.contact_name}</div>
                            <div className="text-sm text-slate-600 font-medium">{seminar?.contact_title}</div>
                        </div>
                    </Reveal>
                    <Reveal delay={0.1}>
                        <div className="mt-10 grid md:grid-cols-3 gap-4 max-w-5xl mx-auto">
                            <a href={`tel:${seminar?.contact_phone}`} className="flex items-center gap-3 rounded-2xl bg-white border border-slate-200 px-5 py-4 hover:border-brand-orange hover:shadow-lift transition-all">
                                <div className="w-11 h-11 rounded-xl bg-orange-gradient flex items-center justify-center shadow-orange shrink-0">
                                    <Phone className="w-5 h-5 text-white" />
                                </div>
                                <div className="min-w-0">
                                    <div className="text-[11px] uppercase tracking-widest text-slate-500 font-bold">Mobile</div>
                                    <div className="font-bold text-navy truncate">{seminar?.contact_phone}</div>
                                </div>
                            </a>
                            <a href={`https://wa.me/${(seminar?.contact_whatsapp || "").replace(/[^\d+]/g, "")}`} target="_blank" rel="noreferrer" className="flex items-center gap-3 rounded-2xl bg-white border border-slate-200 px-5 py-4 hover:border-emerald-500 hover:shadow-lift transition-all">
                                <div className="w-11 h-11 rounded-xl bg-emerald-500 flex items-center justify-center shrink-0">
                                    <MessageCircle className="w-5 h-5 text-white" />
                                </div>
                                <div className="min-w-0">
                                    <div className="text-[11px] uppercase tracking-widest text-slate-500 font-bold">WhatsApp</div>
                                    <div className="font-bold text-navy truncate">{seminar?.contact_whatsapp}</div>
                                </div>
                            </a>
                            <a href={`mailto:${seminar?.contact_email}`} className="flex items-center gap-3 rounded-2xl bg-white border border-slate-200 px-5 py-4 hover:border-brand-orange hover:shadow-lift transition-all">
                                <div className="w-11 h-11 rounded-xl bg-orange-gradient flex items-center justify-center shadow-orange shrink-0">
                                    <Mail className="w-5 h-5 text-white" />
                                </div>
                                <div className="min-w-0">
                                    <div className="text-[11px] uppercase tracking-widest text-slate-500 font-bold">Email</div>
                                    <div className="font-bold text-navy break-all text-sm">{seminar?.contact_email}</div>
                                </div>
                            </a>
                        </div>
                    </Reveal>
                    <Reveal delay={0.2}>
                        <div className="mt-10 text-center">
                            <button data-testid="contact-cta" onClick={scrollToForm} className="btn-gold text-base">
                                Register Now — फक्त ₹{seminar?.registration_fee || "199"} <ChevronRight className="w-5 h-5" />
                            </button>
                        </div>
                    </Reveal>
                </div>
            </section>

            {/* CLOSING VIDEO */}
            {videos?.closing_video_url && (
                <section className="pb-20 bg-white">
                    <div className="container-max">
                        <Reveal>
                            <div className="max-w-3xl mx-auto">
                                <ClickToPlayVideo url={videos.closing_video_url} title="Closing" />
                            </div>
                        </Reveal>
                    </div>
                </section>
            )}

            {/* FOOTER */}
            <footer className="bg-navy-gradient text-white/80 py-12">
                <div className="container-max grid md:grid-cols-3 gap-8">
                    <div>
                        <div className="flex items-center gap-2.5">
                            <div className="w-10 h-10 rounded-xl bg-orange-gradient flex items-center justify-center shadow-orange">
                                <Egg className="w-5 h-5 text-white" />
                            </div>
                            <div className="leading-tight">
                                <div className="font-extrabold text-lg text-white">Energy Plus</div>
                                <div className="text-[10px] uppercase tracking-[0.2em] text-white/60 font-semibold">Premium Brown Eggs</div>
                            </div>
                        </div>
                        <p className="mt-4 text-sm text-white/70">B2B व D2C व्यवसायातून उद्योजक होण्याची सुवर्णसंधी. Franchise & Distribution Business Opportunity.</p>
                    </div>
                    <div>
                        <div className="text-white text-sm font-bold uppercase tracking-widest">Contact</div>
                        <div className="mt-4 text-sm space-y-2 font-medium">
                            <div>{seminar?.contact_name}</div>
                            <div>{seminar?.contact_phone}</div>
                            <div className="break-all">{seminar?.contact_email}</div>
                            <div>{seminar?.venue}</div>
                        </div>
                    </div>
                    <div>
                        <div className="text-white text-sm font-bold uppercase tracking-widest">Quick Links</div>
                        <div className="mt-4 text-sm space-y-2 font-medium">
                            <div><a href="#what" className="hover:text-brand-orange transition-colors">Workshop</a></div>
                            <div><a href="#mentor" className="hover:text-brand-orange transition-colors">Mentor</a></div>
                            <div><a href="#contact" className="hover:text-brand-orange transition-colors">Register</a></div>
                            <div><a href="/admin/login" className="hover:text-brand-orange transition-colors">Admin</a></div>
                        </div>
                    </div>
                </div>
                <div className="container-max mt-10 pt-6 border-t border-white/15 text-xs text-white/50 flex flex-wrap justify-between gap-3">
                    <div>© {new Date().getFullYear()} Energy Plus. All rights reserved.</div>
                    <div>Made for high-conversion business seminars.</div>
                </div>
            </footer>

            <StickyMobileBar
                phone={seminar?.contact_phone}
                whatsapp={seminar?.contact_whatsapp}
                onRegister={scrollToForm}
            />

            {/* QR Payment Modal — opens on every ₹199 Register CTA */}
            <Dialog open={formOpen} onOpenChange={setFormOpen}>
                <DialogContent data-testid="qr-payment-modal" className="max-w-md p-0 overflow-hidden bg-white rounded-3xl">
                    <div className="bg-orange-gradient text-white px-6 py-5 text-center">
                        <div className="text-xs uppercase tracking-widest font-bold text-white/90">Registration Fee</div>
                        <div className="text-4xl font-black mt-1">₹{seminar?.registration_fee || "199"}</div>
                        <DialogTitle className="sr-only">Pay ₹{seminar?.registration_fee || "199"} via UPI</DialogTitle>
                        <DialogDescription className="sr-only">Scan the QR code with any UPI app to complete your registration.</DialogDescription>
                    </div>
                    <div className="p-6">
                        <div className="text-center">
                            <div className="text-lg font-extrabold text-navy marathi">Scan करून Pay करा</div>
                            <div className="text-xs text-slate-500 mt-1">Google Pay · PhonePe · Paytm · कोणतीही UPI app</div>
                        </div>
                        <div className="mt-4 rounded-2xl bg-slate-50 border border-slate-200 p-4 flex items-center justify-center">
                            <img
                                data-testid="qr-image"
                                src="https://customer-assets-4nw71qhi.emergentagent.net/job_2f169c45-0f34-4412-948f-91d97fafb618/artifacts/wp13sv9h_Good%20Win.jpeg"
                                alt="UPI QR Code — Pay ₹199 to Vardhaman Gandhi"
                                className="w-full max-w-[280px] h-auto rounded-xl bg-white"
                                loading="lazy"
                            />
                        </div>
                        <div className="mt-4 flex items-center gap-2 rounded-xl bg-brand-orangeLight border border-brand-orange/20 px-4 py-3">
                            <div className="text-[11px] uppercase tracking-widest text-brand-orange font-bold shrink-0">UPI ID</div>
                            <div className="font-mono text-sm text-navy truncate flex-1">vardhamangandhi15-1@okicici</div>
                            <button
                                onClick={() => {
                                    navigator.clipboard?.writeText("vardhamangandhi15-1@okicici");
                                    setQrCopied(true);
                                    setTimeout(() => setQrCopied(false), 1500);
                                }}
                                className="text-xs font-bold text-brand-orange hover:text-brand-orangeDark shrink-0"
                                data-testid="qr-upi-copy"
                            >
                                {qrCopied ? "Copied ✓" : "Copy"}
                            </button>
                        </div>
                        <div className="mt-5 rounded-xl bg-blue-50 border border-blue-200 px-4 py-3 text-xs text-blue-900 marathi">
                            <strong>Payment नंतर:</strong> screenshot घेऊन WhatsApp वर पाठवा — तुमची seat confirm होईल.
                        </div>
                        <a
                            href={`https://wa.me/${(seminar?.contact_whatsapp || "").replace(/[^\d+]/g, "")}?text=${encodeURIComponent(
                                "नमस्कार, मी ₹" + (seminar?.registration_fee || "199") + " payment केले आहे. Payment screenshot पाठवत आहे."
                            )}`}
                            target="_blank"
                            rel="noreferrer"
                            data-testid="qr-whatsapp-btn"
                            className="mt-4 flex items-center justify-center gap-2 rounded-xl bg-emerald-500 hover:bg-emerald-600 text-white font-bold py-3 transition-colors"
                        >
                            <MessageCircle className="w-4 h-4" />
                            Payment Screenshot WhatsApp वर पाठवा
                        </a>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
}
